import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageCircleQuestion, Moon, Search, Sun } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCompoundSearchIndex, useListings, useVendors } from "@/hooks/useDatabase";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "@/hooks/useTheme";
import { useMediaQuery } from "@/components/ds/useMediaQuery";
import { cn } from "@/lib/utils";
import SearchResults from "@/components/SearchResults";
import SearchSheet from "@/components/SearchSheet";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import SiteLogo from "@/components/site-nav/SiteLogo";
import DesktopNavItem from "@/components/site-nav/DesktopNavItem";
import AccountMenu from "@/components/site-nav/AccountMenu";
import MobileMenu from "@/components/site-nav/MobileMenu";
import { COMMUNITY_LINKS, NAV_SECTIONS, activeNav, searchShortcutLabel } from "@/components/site-nav/navLinks";
import { ICON_BUTTON, MENU_TOGGLE } from "@/components/site-nav/navStyles";

/**
 * The site header (docs/design/pages/navigation.md).
 *
 * - From 1024px: the logo, five labelled sections (Compounds, Compare prices, Tools, Stacks, Guides; three of
 *   them open a short panel of related pages), then a search field, the theme toggle and a quiet Sign in. Ask
 *   joins them from 1280px, where it fits.
 * - Under 1024px: the logo, a search button and a Menu button. The menu (site-nav/MobileMenu.tsx) holds every
 *   destination, Ask, the theme switch and Sign in.
 * - Search: under 640px the full-screen SearchSheet (shared with the homepage hero); from 640px a panel under the
 *   button. Ctrl+K or Cmd+K opens it anywhere.
 * - One item is lit per page, the most specific match (activeNav in site-nav/navLinks.ts).
 *
 * The bar is 56px tall on phones and 64px from 1024px, plus a 1px rule. Pages leave room for exactly that, the
 * announcement strip below is offset by it, and index.html draws the same box (.shell-nav) before any script
 * runs, so the height and the logo block must not change. Nothing in the bar arrives late and moves the rest:
 * Ask keeps its box from the first frame and only turns visible once the chat has loaded, and Sign in and the
 * account button share one fixed-width slot.
 */

const PHONE_SEARCH = "(max-width: 639px)";
const DESKTOP = "(min-width: 1024px)";

type NootSearchItem = { id: string; name: string; slug: string; category: string; effects: string[] };

function openChat() {
  // FloatingAIChat owns the panel and listens for this event; the flag covers a tap that lands before its chunk
  // has loaded.
  (window as typeof window & { __bhgOpenChat?: boolean }).__bhgOpenChat = true;
  window.dispatchEvent(new Event("bhg:open-chat"));
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  // The open desktop panel: a section id, "account", or none. One at a time.
  const [panel, setPanel] = useState<string | null>(null);
  const openSearchAfterMenu = useRef(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const searchButtonRef = useRef<HTMLButtonElement>(null);
  const searchWrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const active = activeNav(location.pathname);
  const isPhone = useMediaQuery(PHONE_SEARCH);
  const isDesktop = useMediaQuery(DESKTOP);
  const { user, signOut } = useAuth();
  const { theme, toggleTheme } = useTheme();

  // Search data loads when the search first opens, not with every page (the compound rows are the light search
  // index). Once loaded it stays cached, so reopening the search does not fetch again.
  const { data: compounds = [] } = useCompoundSearchIndex(searchOpen);
  const { data: vendors = [] } = useVendors();
  const { data: listings = [] } = useListings(undefined, undefined, { enabled: searchOpen });
  const { data: nootropics = [] } = useQuery({
    queryKey: ["noot-search-items"],
    queryFn: async () => {
      const { data, error } = await supabase.from("nootropic_compounds").select("id, name, slug, category, effects").order("name");
      if (error) throw error;
      return (data || []) as NootSearchItem[];
    },
    enabled: searchOpen,
    staleTime: 30 * 60 * 1000,
  });

  const closeSearch = useCallback(() => {
    setQuery("");
    setSearchOpen(false);
    searchButtonRef.current?.focus();
  }, []);

  const openSearch = useCallback(() => {
    setPanel(null);
    // The menu and the search sheet each lock the page's scroll. Open the sheet only after the menu has gone, so
    // the sheet does not record the menu's lock as the state to put back.
    if (mobileOpen) {
      openSearchAfterMenu.current = true;
      setMobileOpen(false);
      return;
    }
    setSearchOpen(true);
  }, [mobileOpen]);

  // Ctrl+K or Cmd+K opens the search anywhere. Not "/": the homepage hero uses it to focus its own search box.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openSearch]);

  // A new page closes everything.
  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setQuery("");
    setPanel(null);
  }, [location.pathname]);

  // The phone menu only exists under 1024px. If the window grows past that, close it (and so unlock the page).
  useEffect(() => {
    if (isDesktop) setMobileOpen(false);
  }, [isDesktop]);

  // The desktop search panel closes on Escape (focus back to its button) and on a press outside it.
  const desktopSearchOpen = searchOpen && !isPhone;
  useEffect(() => {
    if (!desktopSearchOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!searchWrapRef.current?.contains(e.target as Node)) {
        setQuery("");
        setSearchOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeSearch();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [desktopSearchOpen, closeSearch]);

  const topMatch =
    query.trim().length >= 2
      ? compounds.find(
          (c) =>
            c.name.toLowerCase().includes(query.toLowerCase().trim()) ||
            (c.aliases || []).some((a) => a.toLowerCase().includes(query.toLowerCase().trim())),
        )
      : null;

  const openTopMatch = () => {
    if (!topMatch) return;
    closeSearch();
    navigate(`/compound/${topMatch.slug}`);
  };

  const handleSignOut = async () => {
    setPanel(null);
    setMobileOpen(false);
    await signOut();
    navigate("/");
  };

  const signInHref = `/auth?returnTo=${encodeURIComponent(location.pathname)}`;
  const raised = desktopSearchOpen || panel !== null;

  return (
    <>
      <AnimatePresence>
        {desktopSearchOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="fixed inset-0 z-overlay bg-background/60"
            aria-hidden="true"
          />
        )}
      </AnimatePresence>

      {/* Phones: the search is the full-screen sheet the homepage hero uses, drawn outside the <nav> like the
          menu (a fixed child of a fixed bar is laid out against the bar). */}
      {searchOpen && isPhone && (
        <SearchSheet
          query={query}
          onQueryChange={setQuery}
          onClose={closeSearch}
          onEnter={openTopMatch}
          compounds={compounds}
          vendors={vendors}
          listings={listings}
          nootropics={nootropics}
        />
      )}

      <MobileMenu
        open={mobileOpen}
        active={active}
        pathname={location.pathname}
        onClose={() => setMobileOpen(false)}
        onExitComplete={() => {
          if (openSearchAfterMenu.current) {
            openSearchAfterMenu.current = false;
            setSearchOpen(true);
          }
        }}
        onSearch={openSearch}
        onAsk={() => {
          setMobileOpen(false);
          openChat();
        }}
        theme={theme}
        onToggleTheme={toggleTheme}
        signedInEmail={user ? user.email ?? "" : null}
        signInHref={signInHref}
        onSignOut={handleSignOut}
        returnFocusRef={menuButtonRef}
      />

      {/* z-scale (tailwind.config.ts): the header layer, under the one bottom bar. While a desktop panel or the
          desktop search is open the bar rises to the modal layer, above the bottom bar and the search scrim. */}
      <nav
        aria-label="Main navigation"
        className={cn("fixed inset-x-0 top-0 border-b border-border bg-background", raised ? "z-modal" : "z-header")}
      >
        <div className="container mx-auto flex h-14 items-center px-4 lg:h-16">
          <SiteLogo priority />

          <ul className="ml-6 hidden h-full items-center lg:flex xl:ml-10">
            {NAV_SECTIONS.map((section) => (
              <DesktopNavItem
                key={section.id}
                section={section}
                active={active}
                pathname={location.pathname}
                open={panel === section.id}
                onOpenChange={(open) => setPanel(open ? section.id : null)}
                community={section.id === "guides" ? COMMUNITY_LINKS : undefined}
                align={section.id === "guides" ? "end" : "start"}
              />
            ))}
          </ul>

          <div className="ml-auto flex h-full items-center gap-1">
            {/* Ask, from 1280px. Its box is laid out from the first frame and stays invisible (so not focusable)
                until the chat is on the page (FloatingAIChat sets data-chat on <html>), so the bar does not change
                when the chat loads. From 1024 to 1279px there is no room for it next to the labelled search, and
                the chat's own floating button is there; under 1024px Ask is a row in the menu. */}
            <button
              type="button"
              onClick={openChat}
              aria-label="Ask BodyHackGuide or send feedback"
              className="invisible hidden h-11 shrink-0 items-center gap-2 rounded-control px-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:bg-secondary hover:text-foreground xl:inline-flex [html[data-chat]_&]:visible"
            >
              <MessageCircleQuestion className="h-5 w-5 shrink-0" aria-hidden="true" />
              Ask
            </button>

            {/* Search: an icon under 1024px, a field-shaped button from 1024px. */}
            <div ref={searchWrapRef} className="relative flex h-full items-center">
              <button
                ref={searchButtonRef}
                type="button"
                aria-label="Search"
                aria-expanded={searchOpen}
                onClick={() => (searchOpen ? closeSearch() : openSearch())}
                className={cn(ICON_BUTTON, "lg:w-36 lg:justify-start lg:gap-2 lg:bg-secondary lg:px-3 xl:w-56")}
              >
                <Search className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="hidden text-sm lg:inline">Search</span>
                <kbd className="ml-auto hidden rounded border border-border bg-background px-1.5 font-sans text-xs text-muted-foreground xl:[@media(pointer:fine)]:inline">
                  {searchShortcutLabel()}
                </kbd>
              </button>
              <AnimatePresence>
                {desktopSearchOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0, transition: { duration: 0.15, ease: "easeOut" } }}
                    exit={{ opacity: 0, y: -4, transition: { duration: 0.1, ease: "easeIn" } }}
                    className="absolute right-0 top-full mt-2 w-96 max-w-[calc(100vw-2rem)]"
                  >
                    <div className="rounded-xl border border-border bg-popover p-3 shadow-elevated">
                      <input
                        autoFocus
                        type="search"
                        aria-label="Search compounds, tools and vendors"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") openTopMatch();
                        }}
                        placeholder="Search compounds, tools, vendors"
                        className="h-11 w-full rounded-control bg-secondary px-4 text-sm text-foreground outline-none placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    </div>
                    <div className="relative">
                      <AnimatePresence>
                        <SearchResults query={query} compounds={compounds} vendors={vendors} listings={listings} nootropics={nootropics} onClose={closeSearch} />
                      </AnimatePresence>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <button
              type="button"
              onClick={toggleTheme}
              aria-label={theme === "dark" ? "Switch to light theme" : "Switch to dark theme"}
              className={cn(ICON_BUTTON, "hidden lg:inline-flex")}
            >
              {theme === "dark" ? <Sun className="h-5 w-5" aria-hidden="true" /> : <Moon className="h-5 w-5" aria-hidden="true" />}
            </button>

            {/* One fixed-width slot for Sign in and the account button, so the swap when the session loads moves
                nothing. */}
            <div className="hidden w-[4.5rem] justify-end lg:flex">
              {user ? (
                <AccountMenu
                  email={user.email}
                  open={panel === "account"}
                  onOpenChange={(open) => setPanel(open ? "account" : null)}
                  onSignOut={handleSignOut}
                />
              ) : (
                <Link
                  to={signInHref}
                  className="flex h-11 items-center whitespace-nowrap rounded-control px-3 text-sm font-medium text-muted-foreground transition-colors duration-150 hover:text-foreground"
                >
                  Sign in
                </Link>
              )}
            </div>

            <button
              ref={menuButtonRef}
              type="button"
              aria-haspopup="dialog"
              aria-expanded={mobileOpen}
              aria-controls="mobile-nav-panel"
              onClick={() => {
                setSearchOpen(false);
                setMobileOpen(true);
              }}
              className={cn(MENU_TOGGLE, "lg:hidden")}
            >
              <Menu className="h-5 w-5 shrink-0" aria-hidden="true" />
              <span className="max-[359px]:sr-only">Menu</span>
            </button>
          </div>
        </div>
      </nav>
      <AnnouncementBanner />
    </>
  );
}
